import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../redux/network/https";
import { endPoints, queryKeys } from "../../redux/network/constant";

// ── Types ────────────────────────────────────────────────────────────────────

export interface ProjectFile {
  id: number;
  original_name: string;
  stored_name: string;
  extension: string;
  content_type: string;
  category: string;
  size_bytes: number;
  created_at: string;
  uploaded_by_name: string | null;
  client_visible?: boolean;
  is_previewable: boolean;
  folder_id?: number | null;
  folder_name?: string | null;
  folder_path?: string | null;
}

export interface ProjectFolder {
  id: number;
  name: string;
  is_root: boolean;
  parent: number | null;
  children_count: number;
  created_at: string;
  updated_at: string;
}

export interface Breadcrumb {
  id: number;
  name: string;
  is_root: boolean;
}

export interface CurrentFolder {
  id: number;
  name: string;
  is_root: boolean;
  parent: number | null;
  quota_bytes: number;
  used_bytes: number;
}

export interface ProjectFileListResponse {
  folders: ProjectFolder[];
  items: ProjectFile[];
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  current_folder: CurrentFolder;
  breadcrumbs: Breadcrumb[];
  usage: {
    used_bytes: number;
    quota_bytes: number;
  };
  permissions: {
    can_upload: boolean;
    can_delete: boolean;
    can_share: boolean;
    can_manage_folders: boolean;
  };
}

export interface PresignedUploadItem {
  original_name: string;
  upload_url: string;
  fields: Record<string, string>;
  s3_key: string;
  size_bytes: number;
  content_type: string;
  folder_id: number;
}

export interface PresignedUploadResponse {
  files: PresignedUploadItem[];
}

export interface FileShareLink {
  share_url: string;
  token: string;
  expires_at: string;
}

export interface CreateShareLinkPayload {
  projectId: number;
  fileId: number;
  expiryHours?: number;
  expiresAt?: string;
}

export interface ConfirmUploadPayload {
  s3_key: string;
  original_name: string;
  content_type: string;
  size_bytes: number;
  folder_id?: number | null;
}

export interface FileFilters {
  search?: string;
  ordering?: string;
  page?: number;
  page_size?: number;
  category?: string;
  folder_id?: number | null;
}

export interface ApiResponse<T> {
  data: T;
  message: string;
  success: boolean;
}

export interface FolderTreeFile {
  id: number;
  name: string;
  type: "file";
  extension: string;
  category: string;
  size_bytes: number;
}

export interface FolderTreeNode {
  id: number;
  name: string;
  is_root: boolean;
  type: "folder";
  parent_id: number | null;
  children_count: number;
  file_count: number;
  children: FolderTreeNode[];
  files: FolderTreeFile[];
}

export interface FolderTreeResponse {
  tree: FolderTreeNode;
}

// ── Project Files List ────────────────────────────────────────────────────────

const fetchProjectFiles = async ({ queryKey }: { queryKey: readonly unknown[] }) => {
  const [, , projectId, filters] = queryKey as [string, string, number, FileFilters | undefined];

  const params = new URLSearchParams();
  if (filters?.search) params.append("search", filters.search);
  if (filters?.ordering) params.append("ordering", filters.ordering);
  if (filters?.category) params.append("category", filters.category);
  if (filters?.page) params.append("page", String(filters.page));
  if (filters?.folder_id != null) params.append("folder_id", String(filters.folder_id));
  params.append("page_size", String(filters?.page_size ?? 50));

  const qs = params.toString();
  const url = qs
    ? `${endPoints.ProjectFilesList(projectId)}?${qs}`
    : endPoints.ProjectFilesList(projectId);

  const { data } = await axiosInstance.get<ApiResponse<ProjectFileListResponse>>(url);
  return data?.data;
};

export const useProjectFiles = (
  projectId: number | null,
  filters: FileFilters = {},
  enabled = true
) => {
  const filtersKey: Required<FileFilters> = {
    search: filters?.search || "",
    ordering: filters?.ordering || "-created_at",
    category: filters?.category || "",
    page: filters?.page || 1,
    page_size: filters?.page_size || 50,
    folder_id: filters?.folder_id ?? null,
  };

  return useQuery({
    queryKey: queryKeys.ProjectFiles(projectId, filtersKey),
    queryFn: fetchProjectFiles,
    enabled: !!projectId && enabled,
    staleTime: 30 * 1000,
  });
};

// ── Presigned Upload URL ──────────────────────────────────────────────────────

const postPresignUpload = async ({
  projectId,
  files,
  folderId,
}: {
  projectId: number;
  files: { original_name: string; content_type: string; size_bytes: number }[];
  folderId?: number | null;
}) => {
  const { data } = await axiosInstance.post<ApiResponse<PresignedUploadResponse>>(
    endPoints.ProjectFilesPresign(projectId),
    { files, folder_id: folderId ?? null },
  );
  return data?.data;
};

export const usePresignUpload = () => {
  return useMutation({
    mutationFn: postPresignUpload,
  });
};

// ── Confirm Upload ────────────────────────────────────────────────────────────

const postConfirmUpload = async ({
  projectId,
  payload,
}: {
  projectId: number;
  payload: ConfirmUploadPayload;
}) => {
  const { data } = await axiosInstance.post<ApiResponse<ProjectFile>>(
    endPoints.ProjectFilesConfirm(projectId),
    payload,
  );
  return data?.data;
};

export const useConfirmUpload = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postConfirmUpload,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProjectFilesBase(variables.projectId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProjectFolderTree(variables.projectId),
      });
    },
  });
};

// ── Download / Preview URL ───────────────────────────────────────────────────

const fetchFileUrl = async ({
  projectId,
  fileId,
}: {
  projectId: number;
  fileId: number;
}) => {
  const { data } = await axiosInstance.get<ApiResponse<ProjectFile & { download_url: string }>>(
    endPoints.ProjectFileDetail(projectId, fileId),
  );
  return data?.data?.download_url;
};

export const useFileUrl = () => {
  return useMutation({
    mutationFn: fetchFileUrl,
  });
};

// ── Delete File ───────────────────────────────────────────────────────────────

const deleteFile = async ({ projectId, fileId }: { projectId: number; fileId: number }) => {
  const { data } = await axiosInstance.delete<ApiResponse<null>>(
    endPoints.ProjectFileDelete(projectId, fileId),
  );
  return data;
};

export const useDeleteFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteFile,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProjectFilesBase(variables.projectId),
      });
    },
  });
};

// ── Move File ─────────────────────────────────────────────────────────────────

const patchMoveFile = async ({
  projectId,
  fileId,
  folderId,
}: {
  projectId: number;
  fileId: number;
  folderId: number | null;
}) => {
  const { data } = await axiosInstance.patch<ApiResponse<ProjectFile>>(
    endPoints.ProjectFileDetail(projectId, fileId),
    { folder_id: folderId },
  );
  return data?.data;
};

export const useMoveFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchMoveFile,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProjectFilesBase(variables.projectId),
      });
    },
  });
};

// ── Create Share Link ─────────────────────────────────────────────────────────

const postCreateShareLink = async ({
  projectId,
  fileId,
  expiryHours,
  expiresAt,
}: CreateShareLinkPayload) => {
  const payload = expiresAt ? { expires_at: expiresAt } : { expires_in_hours: expiryHours ?? 168 };
  const { data } = await axiosInstance.post<ApiResponse<FileShareLink>>(
    endPoints.ProjectFileShareCreate(projectId, fileId),
    payload,
  );
  return data?.data;
};

export const useCreateShareLink = () => {
  return useMutation({
    mutationFn: postCreateShareLink,
  });
};

// ── Storage Usage ─────────────────────────────────────────────────────────────

const fetchStorageUsage = async ({ queryKey }: { queryKey: readonly unknown[] }) => {
  const [, , projectId] = queryKey as [string, string, number];
  const { data } = await axiosInstance.get<
    ApiResponse<{ used_bytes: number; quota_bytes: number }>
  >(endPoints.ProjectFilesUsage(projectId));
  return data?.data;
};

export const useStorageUsage = (projectId: number | null, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.ProjectFilesUsage(projectId),
    queryFn: fetchStorageUsage,
    enabled: !!projectId && enabled,
    staleTime: 60 * 1000,
  });
};

// ── Folders ───────────────────────────────────────────────────────────────────

const fetchFolderTree = async ({ queryKey }: { queryKey: readonly unknown[] }) => {
  // queryKey: [...ProjectFolderTree(projectId), excludeFolderId]
  //         = ["projects", "folders", projectId, "tree", excludeFolderId]
  const projectId = queryKey[2] as number;
  const excludeId = queryKey[queryKey.length - 1] as number | null;
  const params = new URLSearchParams();
  if (excludeId != null) params.append("exclude_id", String(excludeId));
  const qs = params.toString();
  const url = qs ? `${endPoints.ProjectFoldersTree(projectId)}?${qs}` : endPoints.ProjectFoldersTree(projectId);
  const { data } = await axiosInstance.get<ApiResponse<FolderTreeResponse>>(url);
  return data?.data;
};

export const useFolderTree = (projectId: number | null, excludeFolderId: number | null = null, enabled = true) => {
  return useQuery({
    queryKey: [...queryKeys.ProjectFolderTree(projectId), excludeFolderId],
    queryFn: fetchFolderTree,
    enabled: !!projectId && enabled,
    staleTime: 30 * 1000,
  });
};

const postCreateFolder = async ({
  projectId,
  name,
  parentId,
}: {
  projectId: number;
  name: string;
  parentId: number | null;
}) => {
  const { data } = await axiosInstance.post<ApiResponse<ProjectFolder>>(
    endPoints.ProjectFoldersCreate(projectId),
    { name, parent_id: parentId },
  );
  return data?.data;
};

export const useCreateFolder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postCreateFolder,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProjectFilesBase(variables.projectId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProjectFolderTree(variables.projectId),
      });
    },
  });
};

const patchUpdateFolder = async ({
  projectId,
  folderId,
  name,
  parentId,
}: {
  projectId: number;
  folderId: number;
  name?: string;
  parentId?: number | null;
}) => {
  const payload: Record<string, unknown> = {};
  if (name !== undefined) payload.name = name;
  if (parentId !== undefined) payload.parent_id = parentId;
  const { data } = await axiosInstance.patch<ApiResponse<ProjectFolder>>(
    endPoints.ProjectFolderDetail(projectId, folderId),
    payload,
  );
  return data?.data;
};

export const useUpdateFolder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchUpdateFolder,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProjectFilesBase(variables.projectId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProjectFolderTree(variables.projectId),
      });
    },
  });
};

const deleteFolder = async ({
  projectId,
  folderId,
}: {
  projectId: number;
  folderId: number;
}) => {
  const { data } = await axiosInstance.delete<ApiResponse<{ folder_ids: number[]; file_ids: number[] }>>(
    endPoints.ProjectFolderDetail(projectId, folderId),
  );
  return data;
};

export const useDeleteFolder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteFolder,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProjectFilesBase(variables.projectId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProjectFolderTree(variables.projectId),
      });
    },
  });
};
